import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Display a tax name without duplicating the rate when the name already contains '%'. */
export function taxDisplayName(name: string, rate: number | string): string {
  if (name.includes('%')) return name;
  return `${name} ${Number(rate)}%`;
}

export function formatCurrency(amount: number, currency = 'PKR'): string {
  const safe = (isNaN(amount) || !isFinite(amount)) ? 0 : amount;
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(safe);
}

/** POS-I356: exchange rate as stored (Decimal 12,6): at least 2, at most 6 decimals, no thousands separator noise lost. */
export function formatRate(rate: number | string): string {
  const n = Number(rate);
  if (!isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6, useGrouping: false });
}

/** Money with exactly two decimals (phone totals and discount lines: "Rs 52.50", never "Rs 52.5"). */
export function formatMoney2(amount: number, currency = 'PKR'): string {
  const safe = (isNaN(amount) || !isFinite(amount)) ? 0 : amount;
  return new Intl.NumberFormat('en-PK', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-PK', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(date));
}

/** Format a date without the time component (e.g. "12 May 2024"). */
export function formatDateOnly(date: string | Date): string {
  return new Intl.DateTimeFormat('en-PK', {
    dateStyle: 'medium',
  }).format(new Date(date));
}

export function debounce<T extends (...args: never[]) => unknown>(fn: T, ms = 300): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
}

export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('0')) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

export function validateNTN(ntn: string): boolean {
  return /^\d{7}$/.test(ntn.trim());
}

export function validateCNIC(cnic: string): boolean {
  const digits = cnic.replace(/[-\s]/g, '');
  return /^\d{13}$/.test(digits);
}

export function formatPercent(value: number, decimals = 2): string {
  return `${Number(value).toFixed(decimals)}%`;
}

export function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/** Format a date as relative for recent dates ("2h ago") and absolute for older ones ("12 May"). */
export function formatRelativeDate(date: string | Date): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) {
    return d.toLocaleTimeString('en-PK', { hour: 'numeric', minute: '2-digit' });
  }

  const diffMins = diffMs / (1000 * 60);
  const diffHrs = diffMs / (1000 * 60 * 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${Math.floor(diffMins)} min ago`;
  if (diffHrs < 24 && d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-PK', { hour: 'numeric', minute: '2-digit' });
  }
  if (diffHrs < 48 && d.toDateString() === new Date(now.getTime() - 86400000).toDateString()) {
    return `Yesterday, ${d.toLocaleTimeString('en-PK', { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/** Format a quantity for display (recipe lines, BOM) without rounding small amounts to 0.00. */
/** Turn camelCase / PascalCase account codes into readable chart labels. */
export function humanizeAccountName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Title-case a raw enum for display — ASSET → "Asset", SALES_TAX → "Sales Tax". */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatQuantity(qty: number, maxDecimals = 4): string {
  const n = Number(qty);
  if (!Number.isFinite(n)) return '0';
  if (n % 1 === 0) return String(n);
  const s = n.toFixed(maxDecimals);
  return s.replace(/\.?0+$/, '');
}

/**
 * POS-I105: the number WhatsApp's wa.me links need - digits only, with the country code and no
 * leading 0 or 00. A Pakistani local number (03124567890) becomes 923124567890.
 */
export function toWhatsAppNumber(phone: string | null | undefined, countryCode = '92'): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('0')) return countryCode + digits.slice(1);
  if (digits.length === 10 && !digits.startsWith(countryCode)) return countryCode + digits;
  return digits;
}

/** POS-I149: true for a plausible Pakistani mobile number (03XXXXXXXXX, +923XXXXXXXXX, 3XXXXXXXXX). */
export function isPakistaniMobile(phone: string | null | undefined): boolean {
  return /^923\d{9}$/.test(toWhatsAppNumber(phone));
}
